/**
 * Date-range model shared by the top bar and the analysis drawer.
 *
 * The API accepts either a named window token or an explicit {start,end}, and
 * always reports the comparison period it used. Presets send the token so the
 * server resolves it against the FEED (the latest day that actually has data),
 * which is why a preset is not the same as computing the dates here.
 */
export type WindowToken =
  | 'today'
  | 'yesterday'
  | 'last_7_days'
  | 'last_14_days'
  | 'last_30_days'
  | 'last_60_days'
  | 'last_90_days'
  | 'last_week'
  | 'this_week'
  | 'this_month';

export interface DateRange {
  token?: WindowToken;
  start?: string;
  end?: string;
  /** Custom comparison period — when set, overrides the auto-computed previous period. */
  compare_start?: string;
  compare_end?: string;
}

export type CompareMode = 'auto' | 'custom' | 'last_month' | 'none';

export interface Preset {
  token: WindowToken;
  label: string;
  /** Shown as the bold prefix in the top bar, e.g. "Last 7 Days:". */
  caption: string;
}

/**
 * What every page opens on.
 *
 * ONE constant, not a literal repeated per page. Availability and the shared
 * PageShell already opened on seven days while the three report views opened on
 * thirty, so the same account saw a different window depending on which item in
 * the rail it clicked — and a comparison between two pages meant nothing unless
 * you noticed. Defined here so that can only be true or false once.
 */
export const DEFAULT_RANGE: DateRange = { token: 'last_7_days' };

export const PRESETS: Preset[] = [
  { token: 'today', label: 'Today', caption: 'Today:' },
  { token: 'yesterday', label: 'Yesterday', caption: 'Yesterday:' },
  { token: 'last_7_days', label: 'Last 7 Days', caption: 'Last 7 Days:' },
  { token: 'last_14_days', label: 'Last 14 Days', caption: 'Last 14 Days:' },
  { token: 'last_30_days', label: 'Last 30 Days', caption: 'Last 30 Days:' },
  { token: 'last_60_days', label: 'Last 60 Days', caption: 'Last 60 Days:' },
  { token: 'last_90_days', label: 'Last 90 Days', caption: 'Last 90 Days:' },
  { token: 'this_week', label: 'This Week', caption: 'This Week:' },
  { token: 'last_week', label: 'Last Week', caption: 'Last Week:' },
  { token: 'this_month', label: 'This Month', caption: 'This Month:' },
];

export const captionFor = (r: DateRange): string =>
  r.token ? (PRESETS.find((p) => p.token === r.token)?.caption ?? 'Custom:') : 'Custom:';

export const iso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const parseIso = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
};

/**
 * Month names are FIXED here rather than taken from the runtime locale.
 *
 * `toLocaleDateString('en-GB', { month: 'short' })` returns a 4-letter "Sept"
 * for September and 3 letters for every other month, so a range rendered
 * "29 Aug 2026 - 04 Sept 2026" changed width and alignment for one month of
 * the year. en-US gives "Sep" but reorders the parts to "Sep 04, 2026", which
 * is not the format this UI uses. Naming the months is the only way to get
 * both a stable 3-letter abbreviation and day-month-year order, and it also
 * makes the label independent of whatever locale the browser is set to.
 */
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** "2026-09-04" -> "04 Sep 2026". */
export const fmtShort = (isoDate: string | undefined): string => {
  if (!isoDate) return '—';
  const d = parseIso(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
};

export const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const addMonths = (d: Date, n: number): Date =>
  new Date(d.getFullYear(), d.getMonth() + n, 1);

/** Calendar grid for a month, padded to whole weeks starting Monday. */
export function monthGrid(month: Date): Array<Date | null> {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const lead = (first.getDay() + 6) % 7; // Monday-first
  const cells: Array<Date | null> = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(month.getFullYear(), month.getMonth(), d));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}
